import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from '@pancakeswap/localization'
import BigNumber from "bignumber.js"

import {
  Heading,
  Text,
  Button,
  useToast,
  Box,
  Flex,
  Card,
  FlexGap,
  ArrowBackIcon,
  ArrowForwardIcon,
  Checkbox,
  useMatchBreakpoints,
  Spinner,
} from '@pancakeswap/uikit'

import Page from 'components/Layout/Page'
import { CAKE, NINEINCH } from '@pancakeswap/tokens'
import { ChainDefaultId, ChainId, CurrencyAmount, ERC20Token, mapChainConfig, NATIVE, Pair, WNATIVE } from '@pancakeswap/sdk'
import { useV2BurnerContract } from 'hooks/useContract'
import { ChainMap } from 'config/constants/types'
import useCatchTxError from 'hooks/useCatchTxError'
import { Break, ClickableColumnHeader, PageButtons, TableWrapper } from 'views/Info/components/InfoTables/shared'
import CircleLoader from 'components/Loader/CircleLoader'
import { useSwitchNetwork } from 'hooks/useSwitchNetwork'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useSingleContractMultiMethods } from 'state/multicall/hooks'
import { formatEther, parseEther, parseUnits } from 'viem'
import { useTokenBalances } from 'views/Liquidity/hooks/usePositions'
import { useTokenPrices } from 'views/Info/hooks/useBnbPrices'
import { useAllPoolDataSWR } from 'state/info/hooks'
import { Arrow } from '@pancakeswap/uikit/components/PaginationButton/PaginationButton'
import { useAllTokens, useBuyBurnPairs, useCurrency } from 'hooks/Tokens'
import { DoubleCurrencyLogo } from 'components/Logo'
import { safeGetAddress } from 'utils'
import axios from 'axios'
import { useAllPools, usePools } from 'views/Liquidity/hooks/usePools'

const BurnInfo = styled(Card)`
  border-radius: 8px;
  border: none;
  margin-top: 40px;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  > div {
    padding: 2em;
  }
`

const ResponsiveGrid = styled.div`
  display: grid;
  grid-gap: 1em;
  align-items: center;
  grid-template-columns: 0.2fr 2fr repeat(4, 1fr);
  padding: 8px 24px;
  @media screen and (max-width: 940px) {
    grid-template-columns: 0.2fr 2fr repeat(2, 1fr);
    &  > *:nth-child(4) {
      display: none;
    }
    &  > *:nth-child(5) {
      display: none;
    }
    &  > *:nth-child(6) {
      display: none;
    }
  }
  @media screen and (max-width: 600px) {
    grid-template-columns: 0.2fr 2fr 1fr;
    
    &  > *:nth-child(4) {
      display: none;
    }
    &  > *:nth-child(5) {
      display: none;
    }
    &  > *:nth-child(6) {
      display: none;
    }
  }
`

const BURNER_ADDRESS: ChainMap<string> = mapChainConfig('NineInchBuyAndBurn')

// Reference pairs used to estimate the burn conversion (token -> WPLS -> 9INCH -> BBC).
// They must be loaded regardless of which LP pools the burner contract holds, so they are
// appended to the usePools() query below.
const NINEINCH_WPLS_PAIR = '0x1164dab36cd7036668ddcbb430f7e0b15416ef0b'
const NINEINCH_BBC_PAIR = '0xb543812ddebc017976f867da710ddb30cca22929'

const SORT_FIELD = {
  name: 'name',
  usdValue: 'usdValue',
  amount0: 'amount0',
  amount1: 'amount1',
  balance: 'balance',
}

const formatNumber = (value, precision = 6) => {
  if (!value) return '0'

  const k = 1000
  const sizes = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y']

  const i = value < 1 ? 0 : Math.floor(Math.log(value) / Math.log(k))

  return `${parseFloat((value / k ** i).toFixed(i ? 2 : precision))}${sizes[i]}`
}

// interface Pair {
//   currency: any; 
//   address: string;
//   symbol: string;
//   symbol0: string;
//   symbol1: string;
//   token0: any;    
//   token1: any;    
//   decimals0: number;
//   decimals1: number;
//   liquidity: BigNumber;
//   amount0: BigNumber;
//   amount1: BigNumber;
//   reserve0: BigNumber;
//   reserve1: BigNumber;
//   usd0: number;
//   usd1: number;
//   usdValue: number;
//   balance: BigNumber;
// }

interface PairTokenItemProps {
  pair: any;
  toggleRow: (id: string) => void;
  selectedRows: string[];
}


const PairTokenItem: React.FC<PairTokenItemProps> = ({ pair, toggleRow, selectedRows }) => {
  const currency0 = useCurrency(pair.token0.address)
  const currency1 = useCurrency(pair.token1.address)
  return (
    <Fragment key={pair.address}>
      <ResponsiveGrid>
        <Checkbox
          onChange={() => toggleRow(pair.address)}
          scale="sm"
          checked={selectedRows.includes(pair.address)}
        />
        <FlexGap gap="1em" alignItems="center">
          <DoubleCurrencyLogo currency0={currency0} currency1={currency1} size={32} />
          <Text>{pair.symbol}</Text>
        </FlexGap>
        <Text>${formatNumber(pair.usdValue, 2)}</Text>
        <Text>{formatNumber(pair.amount0, 6)}</Text>
        <Text>{formatNumber(pair.amount1, 6)}</Text>
        <Text>{formatNumber(pair.balance, 6)}</Text>
      </ResponsiveGrid>
      <Break />
    </Fragment>
  );
};



export default function Burn() {
  const { chainId } = useActiveWeb3React()
  const { t } = useTranslation()
  const { toastSuccess } = useToast()
  const { fetchWithCatchTxError, loading: pendingTx } = useCatchTxError()
  // const { callWithOverridePrice } = useCallWithOverridePrice()
  const { switchNetworkAsync } = useSwitchNetwork()
  const tokenPrices = useTokenPrices(chainId, [NINEINCH[chainId].address, CAKE[chainId].address])
  const contractBurner = useV2BurnerContract()
  const pageSize = 20
  const [sortField, setSortField] = useState(SORT_FIELD.usdValue)
  const [sortDirection, setSortDirection] = useState<boolean>(true)
  const [page, setPage] = useState(1)
  const [selectedRows, selectRows] = useState<string[]>([])
  
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsedTime(prevTime => prevTime + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // const [tokenPrices, setTokenPrices] = useState<any>({})
  // const trackedTokenPairs = useTrackedTokenPairs()
  // const tokenPairsWithLiquidityTokens = useMemo(
  //   () => trackedTokenPairs.map((tokens) => ({ liquidityToken: toV2LiquidityToken(tokens), tokens })),
  //   [trackedTokenPairs],
  // )
  // const liquidityTokens = useMemo(
  //   () => tokenPairsWithLiquidityTokens.map((tpwlt) => tpwlt.liquidityToken),
  //   [tokenPairsWithLiquidityTokens],
  // )
  // const [v2PairsBalances,] = useTokenBalancesWithLoadingIndicator(
  //   BURNER_ADDRESS[chainId],
  //   liquidityTokens,
  // )
  // const [v2PairsTotalSupplies,] = useTotalSupplyWithLoadingIndicator(
  //   liquidityTokens,
  // )
  // const liquidityTokensWithBalances = useMemo(() =>
  //   tokenPairsWithLiquidityTokens.filter(({ liquidityToken }) => v2PairsBalances[liquidityToken.address]?.greaterThan('0')),
  //   [tokenPairsWithLiquidityTokens, v2PairsBalances],
  // )
  // const v2Pairs = usePairs(liquidityTokensWithBalances.map(({ tokens }) => tokens))

  // const allV2Pairs = v2Pairs
  //   ?.filter(([pairState, pair]) => pairState === PairState.EXISTS && Boolean(pair))
  //   .map(([, pair]) => pair)

  // useInterval(() => {
  //   fetchTokenPrices
  //   fetchAllDerivedUSDPrice(multiChainName[chainId]).then((prices) => {
  //     setTokenPrices(prices)
  //   })
  // }, 60000)

  const elipsisSymbol = (symbol: string) => symbol.length > 10 ? `${symbol.slice(0, 8)}...` : symbol

  const { data: balances } = useTokenBalances(BURNER_ADDRESS[chainId])
  // const balances = useBuyBurnPairs()
  const pairBalances: { [address: string]: number } = useMemo(() => {
    if (balances) {
      return balances.filter(({ token }) => token.name === "🍆 9 LP").reduce((result, { token, balance }) => ({ ...result, [token.address]: balance }), {})
    }
    return {}
  }, [balances])

  const pairSupplies: { [address: string]: number } = useMemo(() => {
    // if (!isLoading && balanceStatus === "success" && balances) {
    if (balances) {
      return balances.filter(({ token }) => token.name === "🍆 9 LP").reduce((result, { token, totalSupply }) => ({ ...result, [token.address]: totalSupply }), {})
    }
    return {}
  }, [balances])

  // const pairSupplies: { [address: string]: number } = useMemo(() => {
  //   if (!isLoading && balanceStatus === "success" && balances) {
  //     return balances.filter(({ token }) => token.name === "🍆 9 LP").reduce((result, { token }) => ({ ...result, [token.address]: new BigNumber(token.total_supply).shiftedBy(-18).toNumber() }), {})
  //   }
  //   return {}
  // }, [balances, isLoading, balanceStatus])
  const lpAddresses = useMemo(() => Object.keys(pairBalances).filter(Boolean).map(addr => addr.toLowerCase()), [pairBalances])

  const pairQueryAddresses = useMemo(() => [...lpAddresses, NINEINCH_WPLS_PAIR, NINEINCH_BBC_PAIR], [lpAddresses])

  const { data: allV2Pairs, status: pairStatus } = usePools(pairQueryAddresses)

  const [lpTokens, setLpTokens] = useState<any[]>([])
  useEffect(() => {
    // if (balanceStatus !== "success" || pairStatus !== "success")
    //  return

    if (pairBalances === null || Object.keys(pairBalances).length === 0 || !allV2Pairs) {
      return
    }

    const pairs = {}
    // for (const lpPair of pairBalances) {
    for (const lpAddress of Object.keys(pairBalances)) {
      // const lpAddress = lpPair.token.address
      const totalSupply = pairSupplies[lpAddress]
      const balance = pairBalances[lpAddress]
      const share = balance / totalSupply
      const pool = allV2Pairs.find(pair => safeGetAddress(pair.address)===safeGetAddress(lpAddress))

      if (!pool)
        continue
      const amount0 = pool.reserve0 * share
      const amount1 = pool.reserve1 * share
      const usd0 = pool.liquidity * share / 2
      const usd1 = pool.liquidity * share / 2

      pairs[lpAddress] = {
        // currency: pair.liquidityToken,
        address: safeGetAddress(lpAddress),
        symbol: `${elipsisSymbol(pool.token0.symbol)} / ${elipsisSymbol(pool.token1.symbol)}`,
        // symbol0: pool.token0.symbol,
        // symbol1: pool.token1.symbol,
        token0: pool.token0,
        token1: pool.token1,
        // decimals0: pool.token0.decimals,
        // decimals1: pool.token1.decimals,
        totalSupply,
        liquidity: pool.liquidity,
        amount0,
        amount1,
        reserve0: pool.reserve0,
        reserve1: pool.reserve1,
        usd0,
        usd1,
        usdValue: usd0 + usd1,
        balance,
      }
    }

    const pairsObj = Object.values(pairs)
    setLpTokens(pairsObj.filter((pair: any) => pair.amount0 > 10000 && pair.amount1 > 10000))

    // 1000000000000000
    // console.log("pairsObj", pairsObj)
    // const _filteredTokens = []

    // if (pairsObj && pairsObj.length > 0) {
    //   for (let i = 0; i < pairsObj.length; i++) {
    //     try {
    //       const pair = pairsObj[i]
    //       // @ts-ignore
    //       const token0Address: string = pair.token0.address
    //       // @ts-ignore
    //       const token1Address: string = pair.token1.address
    //       const tokens0: string[] = [token0Address]
    //       const tokens1: string[] = [token1Address]

    //       console.log("token0Address", token0Address)
    //       console.log("token1Address", token1Address)
    //       contractBurner.estimateGas.convertLps([tokens0, tokens1]).then((gasEstimation) => {
    //         console.log("gasEstimation", gasEstimation)
    //         if (gasEstimation) {
    //           _filteredTokens.push(pair)
    //         }
    //       }).catch((error) => {
    //         console.error(error)
    //       })
    //     } catch (error) {
    //       console.error(error)
    //     }
    //   }
    // }

    // setLpTokens(_filteredTokens) // .filter((pair: any) => pair.amount0?.gt(10000000000) && pair.amount1?.gt(10000000000))
  }, [allV2Pairs, pairBalances, pairSupplies, pairStatus])

  const maxPage = lpTokens && lpTokens.length ? Math.floor(lpTokens.length / pageSize) + (lpTokens.length % pageSize ? 1 : 0) : 1
  // const contractRouter = useContract(ROUTER_ADDRESS[chainId], abiRouter)

  const handleSort = useCallback(
    (newField: string) => {
      setSortField(newField)
      setSortDirection(sortField !== newField ? true : !sortDirection)
    },
    [sortDirection, sortField],
  )

  const handleSwitch = async (): Promise<void> => {
    await switchNetworkAsync(ChainDefaultId)
  }

  const arrow = useCallback(
    (field: string) => {
      const directionArrow = !sortDirection ? '↑' : '↓'
      return sortField === field ? directionArrow : ''
    },
    [sortDirection, sortField],
  )

  const handleBurn = async () => {
    const tokens0: string[] = lpTokens.filter(pair => selectedRows.includes(pair.address)).map(pair => pair.token0.address)
    const tokens1: string[] = lpTokens.filter(pair => selectedRows.includes(pair.address)).map(pair => pair.token1.address)

    // Create an overrides object with a manual gas limit
    const overrides = {
      gasLimit: 400000,
    };

    const receipt = await fetchWithCatchTxError(() => {
      return contractBurner.write.convertLps([tokens0, tokens1])
    }, {
      type: 'Buy & burn',
      summary: `Buy & burn`
    })
    if (receipt?.status) {
      selectRows([])
      // toastSuccess(
      //   t('Success'),
      //   <ToastDescriptionWithTx txHash={receipt.transactionHash}>{t('Successfully burnt!')}</ToastDescriptionWithTx>,
      // )
    }
  }

  function useBurnConfig() {
    const calls = [
      {
        functionName: 'NineInchBuyBurnPercentage',
      },
      {
        functionName: 'BBCBuyBurnPercentage',
      },
      {
        functionName: 'BOUNTY_FEE',
      },
      {
        functionName: 'burnedNineInch',
      },
      {
        functionName: 'burnedBBC',
      },
    ]
    const [rateNineInch, rateBBC, rateBounty, totalNineInch, totalBBC]: any[] = useSingleContractMultiMethods(contractBurner, calls)
    return {
      rateNineInch: Number(rateNineInch?.result ?? 0n) / 10000,
      rateBBC: Number(rateBBC?.result ?? 0n) / 10000,
      rateBounty: Number(rateBounty?.result ?? 0n) / 10000,
      totalNineInch: totalNineInch?.result,
      totalBBC: totalBBC?.result,
    }
  }

  const status = useBurnConfig()
  const tokens = useAllTokens()
  const pairs = useMemo(() => {
    if (!allV2Pairs) return []
    const list: any[] = Array.isArray(allV2Pairs) ? allV2Pairs : Object.values(allV2Pairs)
    return list.flatMap((pool: any) => {
      try {
        const reserve0 = new BigNumber(pool.reserve0.toString()).shiftedBy(pool.token0.decimals).toFixed(0)
        const reserve1 = new BigNumber(pool.reserve1.toString()).shiftedBy(pool.token1.decimals).toFixed(0)
        return [new Pair(
          CurrencyAmount.fromRawAmount(pool.token0, reserve0),
          CurrencyAmount.fromRawAmount(pool.token1, reserve1),
        )]
      } catch {
        return []
      }
    })
  }, [allV2Pairs])

  const burnState = useMemo(() => {
    let amountETH = 0

    if (selectedRows?.length > 0 && lpTokens && tokenPrices && pairs.length > 0 && chainId) {
      const pair9INCHPLS = pairs.find(pair =>
        pair.token0.address.toLowerCase() === WNATIVE[chainId].address.toLowerCase() && pair.token1.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase() ||
        pair.token1.address.toLowerCase() === WNATIVE[chainId].address.toLowerCase() && pair.token0.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase()
      )
      const pair9INCHBBC = pairs.find(pair =>
        pair.token0.address.toLowerCase() === CAKE[chainId].address.toLowerCase() && pair.token1.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase() ||
        pair.token1.address.toLowerCase() === CAKE[chainId].address.toLowerCase() && pair.token0.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase()
      )
      const priceNineInch = tokenPrices[NINEINCH[chainId].address]
      const priceBBC = tokenPrices[CAKE[chainId].address]
      selectedRows.forEach(id => {
        const token = lpTokens.find(pair => pair.address === id);
        if (!token) {
          console.error(`Token not found for id: ${id}`);
          return;
        }

        const processTokenAmount = (amount, tokenAddress) => {
          if (tokenAddress.toLowerCase() !== WNATIVE[chainId].address.toLowerCase()) {
            const pairWithPLS = pairs.find(pair =>
              pair.token0.address.toLowerCase() === tokenAddress.toLowerCase() && pair.token1.address.toLowerCase() === WNATIVE[chainId].address.toLowerCase() ||
              pair.token1.address.toLowerCase() === tokenAddress.toLowerCase() && pair.token0.address.toLowerCase() === WNATIVE[chainId].address.toLowerCase()
            )
            if (pairWithPLS) {
              try {
                const isZero = pairWithPLS.token0.address.toLowerCase() === tokenAddress.toLowerCase()
                const token0 = isZero ? pairWithPLS.token0 : pairWithPLS.token1
                const [amountOutput] = pairWithPLS.getOutputAmount(CurrencyAmount.fromRawAmount(token0, parseUnits(Number(amount).toFixed(token0.decimals), token0.decimals)))
                amountETH += Number(amountOutput.toExact())
              } catch {
                // no usable liquidity in this pair, skip
              }
            } else {
              const pairWith9INCH = pairs.find(pair =>
                pair.token0.address.toLowerCase() === tokenAddress.toLowerCase() && pair.token1.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase() ||
                pair.token1.address.toLowerCase() === tokenAddress.toLowerCase() && pair.token0.address.toLowerCase() === NINEINCH[chainId].address.toLowerCase()
              )
              if (pairWith9INCH && pair9INCHPLS) {
                try {
                  const isZero = pairWith9INCH.token0.address.toLowerCase() === tokenAddress.toLowerCase()
                  const token0 = isZero ? pairWith9INCH.token0 : pairWith9INCH.token1
                  const [amountOutput0] = pairWith9INCH.getOutputAmount(CurrencyAmount.fromRawAmount(token0, parseUnits(Number(amount).toFixed(token0.decimals), token0.decimals)))
                  const [amountOutput] = pair9INCHPLS.getOutputAmount(amountOutput0)
                  amountETH += Number(amountOutput.toExact())
                } catch {
                  // no usable liquidity in this pair, skip
                }
              }
            }
          } else {
            amountETH += amount
          }
        };

        processTokenAmount(token.amount0, token.token0.address);
        processTokenAmount(token.amount1, token.token1.address);
      })

      let burnNineInch = 0
      let burnBBC = 0
      if (pair9INCHPLS && amountETH > 0) {
        try {
          const [amountOutput9INCH] = pair9INCHPLS.getOutputAmount(CurrencyAmount.fromRawAmount(WNATIVE[chainId], parseEther(amountETH.toFixed(18))))
          burnNineInch = Number(amountOutput9INCH.toExact()) * status.rateNineInch
          if (pair9INCHBBC) {
            const [amountOutputBBC] = pair9INCHBBC.getOutputAmount(amountOutput9INCH)
            burnBBC = Number(amountOutputBBC.toExact()) * status.rateBBC
          }
        } catch {
          // conversion failed, report zero
        }
      }
      const bountyNineInch = burnNineInch * status.rateBounty
      const bountyBBC = burnBBC * status.rateBounty
      const bountyUSD = bountyNineInch * (priceNineInch ?? 0) + bountyBBC * (priceBBC ?? 0)

      return {
        burnNineInch, burnBBC, bountyNineInch, bountyBBC, bountyUSD
      }
    }
    if (selectedRows?.length > 0) {
      console.error('Cannot compute burn state: missing lpTokens, tokenPrices or pairs.');
    }
    return {}
  }, [chainId, lpTokens, selectedRows, status, tokenPrices, pairs])

  // console.log("burnState", status)

  const toggleRow = useCallback((id) => {
    if (selectedRows.includes(id))
      selectRows(selectedRows.filter((i) => i !== id))
    else
      selectRows([...selectedRows, id])
  }, [selectRows, selectedRows])

  const toggleAll = () => {
    if (selectedRows.length === lpTokens.length)
      selectRows([])
    else
      selectRows(lpTokens.map(pair => pair.address))
  }

  const { isMobile } = useMatchBreakpoints()

  const selectedUSD = useMemo(() => lpTokens.reduce((usd, token) => {
    if (token.usdValue && selectedRows.includes(token.address)) {
      return token.usdValue + usd
    }
    return usd
  }, 0), [lpTokens, selectedRows])

  const filteredTokens = useMemo(() =>
    lpTokens.sort((token1, token2) => {
      let sorted = 0;
      if (sortField === SORT_FIELD.balance)
        sorted = (sortDirection ? 1 : -1) * (token2.balance - token1.balance)
      else if (sortField === SORT_FIELD.amount0)
        sorted = (sortDirection ? 1 : -1) * (token2.amount0 - token1.amount0)
      else if (sortField === SORT_FIELD.amount1)
        sorted = (sortDirection ? 1 : -1) * (token2.amount1 - token1.amount1)
      else if (sortField === SORT_FIELD.usdValue)
        sorted = (sortDirection ? 1 : -1) * (token2.usdValue - token1.usdValue)
      return sorted
    }).filter((_token, id) => id >= (page - 1) * pageSize && id < page * pageSize),
    [lpTokens, sortDirection, sortField, page, pageSize]
  )

  return (
    <Page>
      <Flex alignItems="flex-start" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" mb="20px" mt="-50px">
        <Box>
          <Heading as="h1" fontSize="32px" color="secondary" mb="12px">
            {t('Buy & Burn')}
          </Heading>
          <Heading as="h2" scale="md" color="textSubtle">
            {t('Select LPs to buy & burn 9INCH')}
          </Heading>
        </Box>

        {status &&
          <BurnInfo>
            <FlexGap gap="2em" alignItems="baseline" mb={3}>
              <Heading as="h2" scale="lg" color="secondary">
                {t('Burn 9INCH')}
              </Heading>
              <Text fontSize={14} color="textSubtle">
                Total 9INCH burned: {status.totalNineInch ? formatNumber(formatEther(status.totalNineInch)) : 0}🔥
              </Text>
              <Text fontSize={14} color="textSubtle">
                Total BBC burned: {status.totalBBC ? formatNumber(formatEther(status.totalBBC)) : 0}🔥
              </Text>
            </FlexGap>
            <Text small color="textSubtle">
              Selected USD value: ${formatNumber(selectedUSD, 2)}
            </Text>
            <Text small color="textSubtle">
              This would burn: {formatNumber(burnState?.burnNineInch, 2)} 9INCH + {formatNumber(burnState?.burnBBC, 2)} BBC
            </Text>
            <Text color="secondary">
              Bounty: {formatNumber(burnState?.bountyNineInch ?? 0, 2)} 9INCH + {formatNumber(burnState?.bountyBBC ?? 0, 2)} BBC / $
              {formatNumber(burnState?.bountyUSD, 2)}
            </Text>
            {/* {
              selectedRows.length > 0 &&
              <Text small color="textSubtle">
                Estimated Gas Fee: {formatBigNumber(gasState?.estimate)} {NATIVE[chainId].symbol} (${formatBigNumber(gasState?.estimateUSD ?? ZERO, 2)})
              </Text>
            } */}
            {
              chainId === ChainId.PULSE ?
                <Button mt={3} width="100%" onClick={handleBurn} disabled={pendingTx || selectedRows.length === 0}>
                  Burn
                  {pendingTx && <CircleLoader stroke="white" />}
                </Button>
                :
                <Button mt={3} width="100%" onClick={handleSwitch}>
                  Switch Network
                </Button>
            }
          </BurnInfo>}
      </Flex>
      <TableWrapper>
        <ResponsiveGrid>
          <div>
            <Checkbox
              onChange={toggleAll}
              scale="sm"
              checked={selectedRows.length > 0 && selectedRows.length === lpTokens.length}
            />
          </div>
          <Text color="secondary" fontSize="12px" bold textTransform="uppercase">
            {t('LPs')}
          </Text>
          <ClickableColumnHeader
            color="secondary"
            fontSize="12px"
            bold
            onClick={() => handleSort(SORT_FIELD.usdValue)}
            textTransform="uppercase"
          >
            {t('USD Value')} {arrow(SORT_FIELD.usdValue)}
          </ClickableColumnHeader>
          <ClickableColumnHeader
            color="secondary"
            fontSize="12px"
            bold
            onClick={() => handleSort(SORT_FIELD.amount0)}
            textTransform="uppercase"
          >
            {t('Token1 Amount')} {arrow(SORT_FIELD.amount0)}
          </ClickableColumnHeader>
          <ClickableColumnHeader
            color="secondary"
            fontSize="12px"
            bold
            onClick={() => handleSort(SORT_FIELD.amount1)}
            textTransform="uppercase"
          >
            {t('Token2 Amount')} {arrow(SORT_FIELD.amount1)}
          </ClickableColumnHeader>
          <ClickableColumnHeader
            color="secondary"
            fontSize="12px"
            bold
            onClick={() => handleSort(SORT_FIELD.balance)}
            textTransform="uppercase"
          >
            {t('LP Amount')} {arrow(SORT_FIELD.balance)}
          </ClickableColumnHeader>
        </ResponsiveGrid>
        <Break />
        {
          filteredTokens.length === 0 && elapsedTime < 30
            ? <FlexGap gap="16px" alignItems="center" justifyContent="center" py={4} width="100%">
              <Spinner />
              <Text color="textSubtle">{elapsedTime > 30 ? "No LP's atm, come back in a moment" : "Loading..."}</Text>
            </FlexGap>
            : <>
              {
                filteredTokens.map((token) => <PairTokenItem
                  key={token.address}
                  pair={token}
                  toggleRow={toggleRow}
                  selectedRows={selectedRows}
                />)
              }
              <PageButtons>
                <Arrow
                  aria-label="Previous page"
                  style={{ cursor: page === 1 ? 'not-allowed' : 'pointer' }}
                  onClick={() => {
                    setPage(page === 1 ? page : page - 1)
                  }}
                >
                  <ArrowBackIcon color={page === 1 ? 'textDisabled' : 'primary'} />
                </Arrow>
                {
                  maxPage > 0 ?
                    <Text>{t('Page %page% of %maxPage%', { page, maxPage })}</Text> :
                    <Text>{t('No LPs to Burn')}</Text>
                }
                <Arrow
                  aria-label="Next page"
                  style={{ cursor: page >= maxPage ? 'not-allowed' : 'pointer' }}
                  onClick={() => {
                    setPage(page >= maxPage ? page : page + 1)
                  }}
                >
                  <ArrowForwardIcon color={page >= maxPage ? 'textDisabled' : 'primary'} />
                </Arrow>
              </PageButtons>
            </>
        }
      </TableWrapper>
    </Page>
  )
}
